import { useEffect, useState } from 'react'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import TimesIcon from 'src/app/assets/icons/ico-times.svg'
import ExclamationTriangleIcon from 'src/app/assets/icons/ico-exclamation-triangle.svg'
import InfoCircleIcon from 'src/app/assets/icons/ico-info-circle.svg'
import ExclamationCircleIcon from 'src/app/assets/icons/ico-exclamation-circle.svg'

const toastTypes = {
  success: 'success',
  error: 'error',
  warning: 'warning'
} as const

type ToastTypes = keyof typeof toastTypes

type Props = Readonly<{
  message: string
  type: ToastTypes
  duration?: number
  onClose: () => void
  className?: string
}>

function getShadowClass(type: ToastTypes) {
  switch (type) {
    case 'success':
      return 'green-shadow'
    case 'error':
      return 'red-shadow'
    case 'warning':
      return 'yellow-shadow'
    default:
      return ''
  }
}

export default function Toast({ type, message, duration = 3000, onClose, className = '' }: Props) {
  const [visible, setVisible] = useState(true)
  const { t } = useClientSideTranslation(useGetCurrentLanguage())

  useEffect(() => {
    setVisible(true)
    const timer = setTimeout(() => {
      setVisible(false)
      setTimeout(onClose, 800) // Allow time for fade-out transition
    }, duration)
    return () => {
      clearTimeout(timer)
    }
  }, [duration, onClose])

  return (
    <div className={`toast ${visible ? 'visible' : 'hidden'} ${className}`}>
      <div className={`toast__inner ${getShadowClass(type)}`} role="alert">
        <div className="toast__icon">
          {type === 'success' ? (
            <InfoCircleIcon />
          ) : type === 'error' ? (
            <ExclamationCircleIcon />
          ) : (
            <ExclamationTriangleIcon />
          )}
        </div>
        <p className="text">{t(message)}</p>
        <div className="toast__close-button">
          <button data-dismiss="alert" onClick={onClose} type="button">
            <TimesIcon />
          </button>
        </div>
      </div>
    </div>
  )
}
